import React, { useRef, useState } from 'react';

import styled from 'styled-components';

import { useWidgetContext } from 'common/Widgets/Widget/WidgetContext';
import useArrayBoundary from 'utils/hooks/useArrayBoundary';

import { INITIAL_NUMBER_OF_COLUMNS } from './constants';
import { useCMASummaryTableStickyHeader, useSetNumberOfDisplayedColumns } from './customHooks';
import StyledDynamicColumnsTable from './StyledDynamicColumnsTable';
import { CommunityType } from '../../types/communities';

interface ContainerProps {
  paddingTop?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-flow: column;
  border-radius: 16px;
  border-bottom-left-radius: 0;
  background: white;
  padding-top: ${(props) => (props.paddingTop ? props.paddingTop : '20px')};

  .cma-summary {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    tr {
      td {
        &:first-child {
          background-color: var(--background-gray-light) !important;
        }
        &::before {
          background-color: var(--background-gray-light);
        }
        &:nth-child(2) {
          background-color: var(--background-gray-light) !important;
        }
      }
    }
  }

  .amenities {
    th {
      border-bottom: solid 1px var(--line-gray) !important;
    }
    .amenity-category-row {
      td {
        font-family: var(--font-bold);
        font-size: 16px;
        padding-top: 30px !important;
        padding-bottom: 10px !important;
        &::before {
          background-color: white;
        }
      }
    }
    .amenity-name-row {
      td {
        font-family: var(--font-medium);
        font-size: 14px;
        border-bottom: solid 1px var(--line-gray);
        &::before {
          background-color: white;
        }
      }
    }
  }

  .contract-benefits {
    th {
      border-bottom: solid 1px var(--line-gray) !important;
    }

    .ant-table-row {
      &:nth-of-type(2) {
        td {
          font-size: 16px;
          font-family: var(--font-bold);
          padding-top: 30px !important;
        }
      }
      &:last-of-type {
        td {
          border-bottom-color: white !important;
        }
      }
    }
  }

  .graph-widget-table {
    padding-top: 0;
    thead {
      th {
        border-bottom: solid 1px var(--line-gray);
      }
    }
    tbody {
      tr {
        &:not(:first-of-type) {
          td {
            padding-top: 20px !important;
            padding-bottom: 20px !important;

            &.ant-table-cell {
              &:nth-of-type(1),
              &:nth-of-type(2) {
                background: var(--background-gray-light) !important;
              }

              &:first-of-type {
                font-family: var(--font-bold);
              }
            }
            &:last-child {
              &::after {
                display: none !important;
              }
            }
          }
        }
      }
    }
  }
`;

const Title = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  padding-left: 20px;
`;

const Note = styled.div`
  font-size: 12px;
  color: var(--gray-dark);
  word-break: break-word;
  padding-left: 20px;
`;

type Props = {
  title?: string;
  subtitle?: string;
  dataSource: any;
  className?: string;
  community: CommunityType;
  competitors: CommunityType[];
  expandedRowKeys?: string[];
  paddingTop?: string;
  paginateDynamicColumns?: boolean;
  enableCustomStickyHeader?: boolean;
  rowKey?: string;
  setExpandedRowKeys?: (payload: string[]) => void;
  getRowClassname?: (record: any, index: number) => string;
  generateTableColumns: (payload: any) => any;
  showCompetitorAverage?: boolean;
};

const DynamicColumnTable = ({
  title,
  subtitle,
  dataSource,
  className,
  community,
  competitors,
  expandedRowKeys,
  paddingTop,
  paginateDynamicColumns = false,
  enableCustomStickyHeader = false,
  rowKey = 'id',
  setExpandedRowKeys,
  getRowClassname,
  generateTableColumns,
  showCompetitorAverage = false,
}: Props) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [displayedNumberOfCompetitors, setDisplayedNumberOfCompetitors] = useState<number>(INITIAL_NUMBER_OF_COLUMNS);
  const { type, filters } = useWidgetContext();
  const valueDisplay = filters?.valueDisplay;
  const comparisonTypeEnabled = filters?.comparisonTypeEnabled;

  const {
    minimum: competitorsMinIndex,
    maximum: competitorsMaxIndex,
    moveLeft: scrollCompetitorsLeft,
    moveRight: scrollCompetitorsRight,
    resetMinimum,
  } = useArrayBoundary({ initialMinimum: 0, initialMaximum: displayedNumberOfCompetitors - 1 });
  const onResetCallback = (i: number) => {
    setDisplayedNumberOfCompetitors(i);
    resetMinimum();
  };
  useSetNumberOfDisplayedColumns(tableRef, onResetCallback);
  useCMASummaryTableStickyHeader(enableCustomStickyHeader, competitorsMaxIndex);

  const displayedCompetitors = competitors?.filter(
    (competitor: any, index: number) => index >= competitorsMinIndex && index <= competitorsMaxIndex
  );

  const columns = generateTableColumns({
    community,
    competitors,
    filteredCompetitors: displayedCompetitors,
    competitorsMinIndex,
    competitorsMaxIndex,
    displayedNumberOfCompetitors,
    expandedRowKeys,
    type,
    valueDisplay,
    comparisonTypeEnabled,
    paginate: paginateDynamicColumns,
    setExpandedRowKeys,
    scrollCompetitorsLeft,
    scrollCompetitorsRight,
    showCompetitorAverage,
  });

  return (
    <Container ref={tableRef} paddingTop={paddingTop}>
      {Boolean(title?.length) && <Title data-test-id="cma-table-title">{title}</Title>}
      {Boolean(subtitle?.length) && <Note>{subtitle}</Note>}
      <StyledDynamicColumnsTable
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowClassName={getRowClassname}
        scroll={{ y: 'max-content' }}
        rowKey={rowKey}
        className={className}
        sticky
      />
    </Container>
  );
};

export default DynamicColumnTable;
